import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Eye, Save, X, Image, 
  FileText, Tag, Search, LayoutDashboard, LogOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list'); // list, create, edit
  const [editingPost, setEditingPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: '',
    tags: '',
    author: 'AutoPostr Team',
    status: 'draft',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    read_time: 5
  });

  useEffect(() => {
    const token = localStorage.getItem('blog_admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchPosts();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.token) {
        localStorage.setItem('blog_admin_token', data.token);
        setIsAuthenticated(true);
        fetchPosts();
      } else {
        setLoginError(data.detail || 'Invalid credentials');
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('blog_admin_token');
    setIsAuthenticated(false);
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('blog_admin_token');
      const response = await fetch(`${API_URL}/api/blog/admin/posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
    setLoading(false);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title),
      meta_title: title
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('blog_admin_token');
    const url = editingPost 
      ? `${API_URL}/api/blog/admin/posts/${editingPost.id}`
      : `${API_URL}/api/blog/admin/posts`;
    
    const method = editingPost ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });
      
      if (response.ok) {
        fetchPosts();
        resetForm();
        setView('list');
      } else {
        const data = await response.json();
        alert(data.detail || 'Error saving post');
      }
    } catch (error) {
      alert('Error saving post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content,
      featured_image: post.featured_image || '',
      category: post.category || '',
      tags: (post.tags || []).join(', '),
      author: post.author || 'AutoPostr Team',
      status: post.status,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      read_time: post.read_time || 5
    });
    setView('edit');
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    const token = localStorage.getItem('blog_admin_token');
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      alert('Error deleting post');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featured_image: '',
      category: '',
      tags: '',
      author: 'AutoPostr Team',
      status: 'draft',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      read_time: 5
    });
    setEditingPost(null);
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Blog Admin</h1>
            <p className="text-white/60">Sign in to manage your blog posts</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-[#111] rounded-2xl p-8 border border-white/10">
            {loginError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {loginError}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-white/70 text-sm mb-2">Username</label>
              <input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white/70 text-sm mb-2">Password</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
                required
              />
            </div>
            
            <button
              type="submit"
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors"
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Post List View
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <header className="bg-[#111] border-b border-white/10 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <LayoutDashboard className="text-emerald-400" size={24} />
              <h1 className="text-xl font-semibold text-white">Blog Admin</h1>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => { resetForm(); setView('create'); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-colors"
              >
                <Plus size={18} />
                New Post
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 transition-colors"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8">
          {/* Search */}
          <div className="mb-6 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-emerald-500/50"
            />
          </div>

          {/* Posts Table */}
          <div className="bg-[#111] rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-6 py-4 text-white/70 text-sm font-medium">Title</th>
                  <th className="text-left px-6 py-4 text-white/70 text-sm font-medium">Category</th>
                  <th className="text-left px-6 py-4 text-white/70 text-sm font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-white/70 text-sm font-medium">Date</th>
                  <th className="text-right px-6 py-4 text-white/70 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-white/60">Loading...</td>
                  </tr>
                ) : filteredPosts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-white/60">No posts found</td>
                  </tr>
                ) : (
                  filteredPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-white/5">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {post.featured_image && (
                            <img 
                              src={post.featured_image} 
                              alt="" 
                              className="w-12 h-8 object-cover rounded"
                            />
                          )}
                          <span className="text-white font-medium">{post.title}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-white/60">{post.category || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          post.status === 'published' 
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {post.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-white/60 text-sm">
                        {new Date(post.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`/blog/${post.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                          >
                            <Eye size={16} />
                          </a>
                          <button
                            onClick={() => handleEdit(post)}
                            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 rounded-lg hover:bg-red-500/10 text-white/60 hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    );
  }

  // Create/Edit Form
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <header className="bg-[#111] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => { resetForm(); setView('list'); }}
              className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            <h1 className="text-xl font-semibold text-white">
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <button
              onClick={handleSubmit}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-colors"
            >
              <Save size={18} />
              {editingPost ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-white/70 text-sm mb-2">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={handleTitleChange}
              placeholder="Enter post title..."
              className="w-full px-4 py-3 rounded-xl bg-[#111] border border-white/10 text-white text-lg focus:outline-none focus:border-emerald-500/50"
              required
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-white/70 text-sm mb-2">URL Slug</label>
            <div className="flex items-center gap-2">
              <span className="text-white/40">/blog/</span>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                className="flex-1 px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Category */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g. Social Media Tips"
                className="w-full px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
              />
            </div>

            {/* Read Time */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Read Time (minutes)</label>
              <input
                type="number"
                value={formData.read_time}
                onChange={(e) => setFormData(prev => ({ ...prev, read_time: parseInt(e.target.value) }))}
                className="w-full px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
              />
            </div>
          </div>

          {/* Featured Image */}
          <div>
            <label className="block text-white/70 text-sm mb-2">Featured Image URL</label>
            <input
              type="url"
              value={formData.featured_image}
              onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
              placeholder="https://..."
              className="w-full px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
            />
            {formData.featured_image && (
              <img src={formData.featured_image} alt="Preview" className="mt-2 h-32 rounded-lg object-cover" />
            )}
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-white/70 text-sm mb-2">Excerpt (Short Description)</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value, meta_description: e.target.value }))}
              placeholder="Brief summary of the post..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50 resize-none"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-white/70 text-sm mb-2">Content (HTML supported)</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Write your post content here... HTML tags are supported."
              rows={15}
              className="w-full px-4 py-3 rounded-xl bg-[#111] border border-white/10 text-white font-mono text-sm focus:outline-none focus:border-emerald-500/50 resize-y"
              required
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-white/70 text-sm mb-2">Tags (comma separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="social media, marketing, tips"
              className="w-full px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
            />
          </div>

          {/* SEO Section */}
          <div className="pt-6 border-t border-white/10">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <FileText size={18} />
              SEO Settings
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Meta Title</label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                  placeholder="SEO optimized title"
                  className="w-full px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-2">Meta Description</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                  placeholder="Description for search engines (150-160 characters)"
                  rows={2}
                  className="w-full px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-white/70 text-sm mb-2">Meta Keywords</label>
                <input
                  type="text"
                  value={formData.meta_keywords}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_keywords: e.target.value }))}
                  placeholder="keyword1, keyword2, keyword3"
                  className="w-full px-4 py-2 rounded-lg bg-[#111] border border-white/10 text-white focus:outline-none focus:border-emerald-500/50"
                />
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default BlogAdminPage;
